import React, { useState } from 'react'
import LocalizedText from '../../presentational/LocalizedText';
import useTranslate from "../../../hooks/useTranslate";
import Button from "../../presentational/Button";
import Input from "../../presentational/Input";
import PasswordTippy from "../../PasswordTippy";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/pro-regular-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

interface ResetPasswordProps {
  serverError: string;
  isLoading: boolean;
  emailOrPhone: string;
  validatePasswordRequirements: (password: string) => Promise<string>;
  finishPasswordReset: (password: string) => void
  isValidPassword: (password: string) => boolean
  setEmailOrPhone: (emailOrPhone: string) => void
}

const ResetPassword: React.FC<ResetPasswordProps> = ({
  serverError,
  isLoading,
  emailOrPhone,
  validatePasswordRequirements,
  finishPasswordReset,
  isValidPassword,
}) => {
  const [unmaskPassword, setUnmaskPassword] = useState(false)
  const [password, setPassword] = useState('')
  const [passwordValid, setPasswordValid] = useState(false);
  const translations = useTranslate([
    "participant-login-password-label", 
    "participant-login-password-placeholder", 
    "participant-login-next-label",
    "participant-login-password-requirements-title",
    "participant-login-password-requirement-length",
    "participant-login-password-requirement-classes",
  ])

  const onSetPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event?.target?.value)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    finishPasswordReset(password)
    return false
  }

  return (
    <form className="login-action" onSubmit={handleSubmit}>
      <h3>
        <LocalizedText textKey="participant-login-reset-password-title" />
      </h3>
      <p>
        <LocalizedText textKey="participant-login-reset-password-info" values={{ emailOrPhone: emailOrPhone }} />
      </p>
      {
        serverError && (
          <p className="server-error">{serverError}</p>
        )
      }
      <div className="field-group">
        <label htmlFor="resetPassword" className="login-field">{translations["participant-login-password-label"]}</label>
        <div className="combo-input-group">
          <PasswordTippy
            password={password}
            passwordValid={passwordValid}
            setPasswordValid={setPasswordValid}
            validatePasswordRequirements={validatePasswordRequirements}
            requirementsTitle={translations["participant-login-password-requirements-title"]}
            requirementLengthText={translations["participant-login-password-requirement-length"]}
            requirementClassesText={translations["participant-login-password-requirement-classes"]}>
            <Input
              onChange={onSetPassword}
              autoComplete="new-password"
              className="autofocus"
              disabled={isLoading}
              id="resetPassword"
              type={unmaskPassword ? 'text' : 'password'}
              value={password}
              placeholder={translations["participant-login-password-placeholder"]}
              fullWidth={true}
              htmlAttributes={{
                onInput: onSetPassword,
                spellCheck: false,
                autoCorrect: "off",
                autoCapitalize: "none",
                maxLength: 64,
              }}
            />
          </PasswordTippy>
          <button
            type="button"
            onClick={() => setUnmaskPassword(!unmaskPassword)}
            className="password-mask-toggle"
            title="Toggle password visibility">
               <FontAwesomeIcon icon={unmaskPassword ? faEye as IconProp: faEyeSlash as IconProp} />
          </button>
        </div>
      </div>
      <Button
        onClick={handleSubmit}
        disabled={!isValidPassword(password)}
        className="primary-action"
        width="100%"
        loading={isLoading}
        type='submit'
        intent='primary'
      >
        <span dangerouslySetInnerHTML={{ __html: translations["participant-login-next-label"] }} />
      </Button>
    </form>
  )
}

export default ResetPassword